import { useState } from "react";
import { Navbar } from "../../../components/layout/Navbar";
import { Footer } from "../../../components/layout/Footer";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Search, Target, TrendingUp, Filter } from "lucide-react";
import { useConvexProjects } from "../../../services/convexService";
import { Link } from "react-router-dom";
import CurrencyDisplay from "../../../components/currency/CurrencyDisplay";

const Projects = () => {
  const { getProjectsByStatus } = useConvexProjects();
  // Get approved projects and filter out those with sell purpose
  const allProjects = getProjectsByStatus("approved")?.filter(project => project.projectPurpose !== "sell") || [];

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("الكل");

  // Get unique categories from projects
  const categories = ["الكل", ...new Set(allProjects.map(project => project?.category))];

  const filteredProjects = allProjects.filter((project) => {
    if (!project) return false;
    const matchesSearch = project.title.includes(searchTerm) || project.description.includes(searchTerm);
    const matchesCategory = selectedCategory === "الكل" || project.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen" dir="rtl">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="py-12 sm:py-16 bg-hero-gradient text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl">
              <span className="text-sm font-semibold text-primary-foreground uppercase tracking-wide animate-fade-in-down" style={{ animationDelay: '0.1s' }}>
                فرص الاستثمار
              </span>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-3 mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                مشاريع تنتظر استثمارك
              </h1>
              <p className="text-base sm:text-lg text-primary-foreground/80 leading-relaxed mb-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                اكتشف مجموعة متنوعة من المشاريع الواعدة في مختلف القطاعات، مع تقارير مفصلة وتقييمات من خبرائنا
              </p>
              <p className="text-sm sm:text-base text-primary-foreground/80 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
                <strong>ستثمر اليوم وغيّر وضعك المالي خلال 6 أشهر</strong><br className="sm:hidden" />
                لا تضيع دقيقة واحدة..! حقق أرباحاً ثابتة ستغير وضعك!
              </p>

            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="py-8 border-b border-border">
          <div className="container mx-auto px-4">
            <div className="flex flex-col gap-4">
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="ابحث عن مشروع..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pr-10 w-full"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full">
                  <Filter className="w-4 h-4 ml-2" />
                  <SelectValue placeholder="القطاع" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.length > 0 ? (
                filteredProjects.map((project) => (
                  <Link to={`/projects/${project._id}`} key={project._id} className="block">
                    <div
                      className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-elevated transition-all duration-300 group"
                    >
                      <div className="p-4 sm:p-6 pb-4">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-4 gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {project.category}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            قيد التمويل
                          </Badge>
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                          {project.title}
                        </h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          {project.description}
                        </p>
                      </div>

                      <div className="p-4 sm:p-6 pt-4 bg-muted/30 border-t border-border">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Target className="w-4 h-4 text-primary" />
                            <span className="text-muted-foreground">المطلوب:</span>
                            <span className="font-semibold"><CurrencyDisplay amount={project.fundingGoal || 0} /></span>
                          </div>
                          <div className="flex items-center gap-1 text-sm">
                            <TrendingUp className="w-4 h-4 text-secondary" />
                            <span className="font-semibold text-secondary">{project.expectedROI}% ROI</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-muted-foreground">التقدم</span>
                          <span className="font-semibold">{Math.round((project.currentFunding / project.fundingGoal) * 100)}%</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-hero-gradient rounded-full transition-all duration-500"
                            style={{ width: `${Math.round((project.currentFunding / project.fundingGoal) * 100)}%` }}
                          />
                        </div>
                      </div>

                      <div className="p-4 sm:p-6 pt-4">
                        <Button className="w-full bg-hero-gradient hover:opacity-90 text-primary-foreground">
                          عرض التفاصيل
                        </Button>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="text-center py-16 col-span-full">
                  <p className="text-muted-foreground text-lg">لا توجد مشاريع تطابق البحث</p>
                </div>
              )}
            </div>

            {filteredProjects.length === 0 && allProjects.length > 0 && (
              <div className="text-center py-16">
                <p className="text-muted-foreground text-lg">لا توجد مشاريع تطابق البحث</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Projects;